<script setup>
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { computed, ref, watch } from 'vue';
import { CsButton } from '../button';
import { CsIcon } from '../icon';
import { CsText } from '../text';
import DateItem from './components/DateItem.vue';

/**
 * @typedef {object} CsDatePickerProps
 * @property {Date} [value] - The selected date value (for single date mode)
 * @property {Date} [initialDate] - Initial date to display (defaults to today)
 * @property {Date} [disableDatesBefore] - Disable all dates before this date (exclusive)
 * @property {Date} [disableDatesAfter] - Disable all dates after this date (exclusive)
 * @property {Date[]} [disabledDates] - Array of specific dates to disable
 * @property {boolean} [isRange=false] - Enable date range selection mode
 * @property {Date} [startDate] - Start date of the range (for range mode)
 * @property {Date} [endDate] - End date of the range (for range mode)
 * @property {boolean} [isMultimonth=false] - Display two months side by side
 */

/**
 * @type {CsDatePickerProps}
 */
const props = defineProps({
  value: {
    type: Date,
    default: null,
  },
  initialDate: {
    type: Date,
    default: () => new Date(),
  },
  disableDatesBefore: {
    type: Date,
    default: null,
  },
  disableDatesAfter: {
    type: Date,
    default: null,
  },
  disabledDates: {
    type: Array,
    default: () => [],
  },
  isRange: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: [String, Date],
    default: null,
  },
  endDate: {
    type: [String, Date],
    default: null,
  },
  isMultimonth: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'change', 'select', 'range-select']);

// Reactive state
const currentDate = ref(props.initialDate || new Date());
const selectedDate = ref(props.value);

// Range selection state
const hoveredDate = ref(null);
const isDateRangeSelectionActive = ref(false);
const tempStartDate = ref(props.startDate);
const tempEndDate = ref(props.endDate);

// Initialize multi-month positioning if we have initial dates
// Only change positioning if we have an end date
if (props.isMultimonth && props.endDate) {
  // Set currentDate to end date's month so it appears on the right
  currentDate.value = new Date(props.endDate);
}

// Watch for prop changes
watch(
  () => props.startDate,
  newStartDate => {
    tempStartDate.value = newStartDate;
    if (newStartDate && !tempEndDate.value) {
      isDateRangeSelectionActive.value = true;
    } else {
      isDateRangeSelectionActive.value = false;
    }

    // Update currentDate for multi-month positioning when start date changes
    updateCurrentDateForMultiMonth();
  }
);

watch(
  () => props.endDate,
  newEndDate => {
    tempEndDate.value = newEndDate;
    if (tempStartDate.value && newEndDate) {
      isDateRangeSelectionActive.value = false;
    }

    // Update currentDate for multi-month positioning when end date changes
    updateCurrentDateForMultiMonth();
  }
);

// Function to update currentDate for optimal multi-month positioning
function updateCurrentDateForMultiMonth() {
  if (!props.isMultimonth) return;

  const currentStartDate = tempStartDate.value;
  const currentEndDate = tempEndDate.value;

  // Only change month positioning when we have both start and end dates
  // Do NOT change positioning when only start date is selected
  if (currentStartDate && currentEndDate) {
    // Set currentDate to the end date's month
    // Since our multi-month shows [currentDate-1, currentDate],
    // this will show [endDate-1 month, endDate month] = [startDate month, endDate month] in most cases
    currentDate.value = new Date(currentEndDate);
  }
  // Removed the else if (currentStartDate) block - no positioning change for start date only
}

// Computed properties
const currentMonthYear = computed(() => {
  return format(currentDate.value, 'MMMM yyyy');
});

const calendarDays = computed(() => {
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  return eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });
});

const multiMonthData = computed(() => {
  if (!props.isMultimonth) return [];

  const months = [];

  // For multi-month view, show previous month (left) and current month (right)
  // This starts from -1 (previous month) to 0 (current month)
  for (let i = -1; i < 1; i++) {
    // Show 2 months: previous and current
    const monthDate = addMonths(currentDate.value, i);
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days = eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });

    months.push({
      date: monthDate,
      title: format(monthDate, 'MMMM yyyy'),
      days,
    });
  }

  return months;
});

const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Methods
function isDateDisabled(date) {
  // Check if date is before the disableDatesBefore threshold
  if (props.disableDatesBefore && isBefore(date, props.disableDatesBefore)) {
    return true;
  }

  // Check if date is after the disableDatesAfter threshold
  if (props.disableDatesAfter && isAfter(date, props.disableDatesAfter)) {
    return true;
  }

  // Check if date is in the disabledDates array
  if (props.disabledDates && props.disabledDates.length > 0) {
    return props.disabledDates.some(disabledDate =>
      isSameDay(date, disabledDate)
    );
  }

  return false;
}

function goToPreviousMonth() {
  currentDate.value = subMonths(currentDate.value, props.isMultimonth ? 2 : 1);
}

function goToNextMonth() {
  currentDate.value = addMonths(currentDate.value, props.isMultimonth ? 2 : 1);
}

function selectDate(date) {
  if (props.isRange) {
    // Range mode logic
    if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
      // Start new range selection
      tempStartDate.value = date;
      tempEndDate.value = null;
      isDateRangeSelectionActive.value = true;

      // Emit range selection
      emit('range-select', {
        startDate: tempStartDate.value,
        endDate: tempEndDate.value,
      });

      // Do NOT update month positioning when only start date is selected
      // Month positioning will only change when end date is selected
    } else if (tempStartDate.value && !tempEndDate.value) {
      // Complete range selection
      // Always allow completing the range, even with the same date
      if (isBefore(date, tempStartDate.value)) {
        // Swap if end date is before start date
        tempEndDate.value = tempStartDate.value;
        tempStartDate.value = date;
      } else {
        tempEndDate.value = date;
      }
      isDateRangeSelectionActive.value = false;

      // Emit range selection
      emit('range-select', {
        startDate: tempStartDate.value,
        endDate: tempEndDate.value,
      });

      // Update month positioning for multi-month view
      updateCurrentDateForMultiMonth();
    }
  } else {
    // Single date mode
    selectedDate.value = date;
    emit('input', date);
    emit('change', date);
    emit('select', date);
  }
}

function getDayVariant(date, monthDate = null) {
  if (isDateDisabled(date)) {
    return 'disabled';
  }

  if (props.isRange) {
    // Range mode variants
    const currentStartDate = tempStartDate.value;
    const currentEndDate = tempEndDate.value;

    // If we have a complete range, show range styling
    if (currentStartDate && currentEndDate) {
      // Check if start and end dates are the same (single date range)
      if (
        isSameDay(currentStartDate, currentEndDate) &&
        isSameDay(date, currentStartDate)
      ) {
        return 'range-single';
      }

      if (isSameDay(date, currentStartDate)) {
        return 'range-start';
      }
      if (isSameDay(date, currentEndDate)) {
        return 'range-end';
      }
      if (
        isWithinInterval(date, { start: currentStartDate, end: currentEndDate })
      ) {
        return 'range-between';
      }
    }

    // If we have start date but no end date, show hover preview
    if (
      currentStartDate &&
      !currentEndDate &&
      hoveredDate.value &&
      isDateRangeSelectionActive.value
    ) {
      const previewStart = isBefore(hoveredDate.value, currentStartDate)
        ? hoveredDate.value
        : currentStartDate;
      const previewEnd = isBefore(hoveredDate.value, currentStartDate)
        ? currentStartDate
        : hoveredDate.value;

      if (isSameDay(date, previewStart)) {
        return 'range-start';
      }
      if (isSameDay(date, previewEnd)) {
        return 'range-end';
      }
      if (isWithinInterval(date, { start: previewStart, end: previewEnd })) {
        return 'range-between';
      }
    }

    // Show active for start date when no end date selected
    if (currentStartDate && isSameDay(date, currentStartDate)) {
      return 'active';
    }
  } else {
    // Single date mode
    if (selectedDate.value && isSameDay(date, selectedDate.value)) {
      return 'active';
    }
  }

  // For multi-month view, check if date belongs to the current month being rendered
  if (monthDate && !isSameMonth(date, monthDate)) {
    return 'disabled';
  }
  // For single month view, use the original logic
  if (!monthDate && !isSameMonth(date, currentDate.value)) {
    return 'disabled';
  }

  return 'default';
}

function isDayClickable(date, monthDate = null) {
  const targetMonth = monthDate || currentDate.value;
  return isSameMonth(date, targetMonth) && !isDateDisabled(date);
}

function handleDateHover(date, monthDate = null) {
  if (
    props.isRange &&
    isDateRangeSelectionActive.value &&
    isDayClickable(date, monthDate)
  ) {
    hoveredDate.value = date;
  }
}

function handleDateLeave() {
  if (props.isRange && isDateRangeSelectionActive.value) {
    hoveredDate.value = null;
  }
}
</script>

<template>
  <div class="inline-flex">
    <!-- Single Month View -->
    <div v-if="!isMultimonth">
      <!-- Header with navigation -->
      <header class="flex items-center justify-between mb-4">
        <div>
          <CsButton variant="tertiary" is-icon-only @click="goToPreviousMonth">
            <CsIcon name="arrow-left" variant-color="secondary" />
          </CsButton>
        </div>

        <CsText weight="medium">
          {{ currentMonthYear }}
        </CsText>

        <div>
          <CsButton variant="tertiary" is-icon-only @click="goToNextMonth">
            <CsIcon name="arrow-right" variant-color="secondary" />
          </CsButton>
        </div>
      </header>

      <!-- Week days header -->
      <div class="grid grid-cols-7 gap-1 mb-2">
        <div
          v-for="day in weekDays"
          :key="day"
          class="h-8 flex items-center justify-center text-xs font-medium text-brand-gray-12"
        >
          {{ day }}
        </div>
      </div>

      <!-- Calendar grid -->
      <div class="grid grid-cols-7 gap-0">
        <DateItem
          v-for="date in calendarDays"
          :key="date.toISOString()"
          :variant="getDayVariant(date)"
          :disabled="!isDayClickable(date)"
          :show-content="isSameMonth(date, currentDate)"
          @click="isDayClickable(date) && selectDate(date)"
          @mouseenter="handleDateHover(date)"
          @mouseleave="handleDateLeave"
        >
          {{ isSameMonth(date, currentDate) ? format(date, 'd') : '' }}
        </DateItem>
      </div>
    </div>

    <!-- Multi-Month View -->
    <div v-else>
      <!-- Multi-month calendar grid -->
      <div class="grid grid-cols-2 gap-4">
        <div
          v-for="(month, index) in multiMonthData"
          :key="month.date.toISOString()"
          class="space-y-2"
        >
          <!-- Header with navigation for each month -->
          <header class="flex items-center justify-between mb-4">
            <div>
              <!-- Only show left arrow on first month -->
              <CsButton
                v-if="index === 0"
                variant="tertiary"
                is-icon-only
                @click="goToPreviousMonth"
              >
                <CsIcon name="arrow-left" variant-color="secondary" />
              </CsButton>
              <div v-else class="w-8 h-8" />
            </div>

            <CsText weight="medium">
              {{ month.title }}
            </CsText>

            <div>
              <!-- Only show right arrow on last month -->
              <CsButton
                v-if="index === multiMonthData.length - 1"
                variant="tertiary"
                is-icon-only
                @click="goToNextMonth"
              >
                <CsIcon name="arrow-right" variant-color="secondary" />
              </CsButton>
              <div v-else class="w-8 h-8" />
            </div>
          </header>

          <!-- Week days header -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div
              v-for="day in weekDays"
              :key="day"
              class="h-8 flex items-center justify-center text-xs font-medium text-brand-gray-12"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar grid for this month -->
          <div class="grid grid-cols-7 gap-0">
            <DateItem
              v-for="date in month.days"
              :key="date.toISOString()"
              :variant="getDayVariant(date, month.date)"
              :disabled="!isDayClickable(date, month.date)"
              :show-content="isSameMonth(date, month.date)"
              @click="isDayClickable(date, month.date) && selectDate(date)"
              @mouseenter="handleDateHover(date, month.date)"
              @mouseleave="handleDateLeave"
            >
              {{ isSameMonth(date, month.date) ? format(date, 'd') : '' }}
            </DateItem>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
